import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';
import { useSite } from '../lib/SiteContentContext';
import { BOOK_TRIAL_URL } from '../lib/config';
import TrustpilotBadge from './TrustpilotBadge';

export default function Hero() {
  const { CTA } = useSite();
  return (
    <section className="relative overflow-hidden" aria-labelledby="hero-heading">
      {/* Soft gradient background */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-butter/60 via-brand-cream to-brand-cream" />
      <div className="absolute -z-10 top-16 -left-24 w-80 h-80 bg-brand-mint/60 blur-3xl rounded-full animate-blob" />
      <div className="absolute -z-10 top-32 right-0 w-72 h-72 bg-brand-rose/70 blur-3xl rounded-full animate-blob" />

      <div className="container pt-8 pb-14 lg:pt-14 lg:pb-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-ink/10 px-3.5 py-1.5 text-xs md:text-sm text-brand-ink shadow-sm">
            <span className="w-2 h-2 rounded-full bg-brand-coral animate-pulse" aria-hidden="true" />
            Enrolment open for the new academic year
          </div>

          <h1
            id="hero-heading"
            className="mt-5 font-display text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-brand-ink break-words"
          >
            Science, <span className="italic text-brand-teal">sorted.</span>
            <br />
            <span className="relative inline-block">
              Grades,
              <svg className="absolute -bottom-2 md:-bottom-3 left-0 w-full" viewBox="0 0 300 20" fill="none" aria-hidden="true">
                <path d="M2 15 C 80 2, 180 2, 298 12" stroke="#FFC200" strokeWidth="6" strokeLinecap="round" />
              </svg>
            </span>{' '}
            unlocked.
          </h1>

          <p className="mt-6 text-base md:text-lg text-brand-ink/75 max-w-xl">
            Live, interactive, and structured GCSE and A Level science tuition, taught by qualified UK teachers with current examining experience.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
            <Button
              asChild
              size="lg"
              className="bg-brand-sun text-brand-ink hover:bg-brand-ink hover:text-brand-sun rounded-full h-12 px-6 font-semibold shadow-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-ink"
            >
              <a
                href={BOOK_TRIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="gap-2"
              >
                {CTA.primary} <ArrowUpRight className="w-4 h-4" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full h-12 px-6 bg-white border-brand-ink/20 text-brand-ink hover:bg-white hover:border-brand-ink"
            >
              <Link to="/courses">{CTA.secondary}</Link>
            </Button>
          </div>

          {/* Compact Trustpilot summary near hero */}
          <div className="mt-6">
            <TrustpilotBadge />
          </div>
        </div>

        {/* Visual side */}
        <div className="relative order-first lg:order-last" data-testid="hero-visual">
          <div className="relative mx-auto w-full max-w-[640px] lg:max-w-none">
            <img
              src="/hero/hero-meet.webp"
              alt="Science Sorted Tutoring - Google Meet lesson with Qasid Ahmed teaching an A Level Biology exam question on biochemical tests for reducing sugars"
              className="block w-full h-auto rounded-3xl shadow-2xl select-none ring-1 ring-brand-ink/10"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
